import { useNavigate } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { CustomFileUpload, CustomInput } from '../Components/CustomFormElements';
import './LoginPage.css';

// ✅ Validation Function
const validateFields = (fields, validationRules, showAlert) => {
  let isValid = true;

  fields.forEach((fieldId) => {
    const field = document.getElementById(fieldId);
    const rules = validationRules[fieldId] || {};
    const value = field?.value?.trim() || '';

    if (field) {
      field.addEventListener(
        'focus',
        () => {
          field.classList.remove('input-error');
        },
        { once: true },
      );
    }

    // Check required
    if (rules.required && !value) {
      field.classList.add('input-error');
      showAlert('fail', rules.errorMessage || 'This field is required.');
      isValid = false;
      return;
    }

    // Check regex
    if (rules.regex && !rules.regex.test(value)) {
      field.classList.add('input-error');
      showAlert('fail', rules.errorMessage || 'Invalid input.');
      isValid = false;
      return;
    }

    // If passed all checks
    field.classList.remove('input-error');
  });

  return isValid;
};

// ✅ Sign-Up Page Component
const OrgSettingsPageContent = ({ canEdit }) => {
  console.log('canEdit value:', canEdit);
  const { showAlert } = useAlert();
  const navigate = useNavigate(); // Hook for navigation
  const { showConfirmation } = useConfirmation(); // Hook for confirmation dialog

  // ✅ Handle Cancel Logic with Confirmation
  const handleCancel = () => {
    showConfirmation('Are you sure you want to cancel and go back to the login page?', () => {
      navigate('/login'); // Redirect to login on confirmation
    });
  };

  // ✅ Validation Rules
  const validationRules = {
    name: {
      required: true,
      regex: /^[^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+$/,
      errorMessage: 'Enter a valid name. No spaces or special characters allowed.',
    },
    'last-name': {
      required: true,
      regex: /^[^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+$/,
      errorMessage: 'Enter a valid last name. No spaces or special characters allowed.',
    },
    email: {
      required: true,
      regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      errorMessage: 'Enter a valid email address.',
    },
    password1: {
      required: true,
      regex: /^(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~]){2,}(?=.*\d){2,}.{10,}$/,
      errorMessage: 'Password must have at least 10 characters, 2 special symbols, and 2 numbers.',
    },
    password2: {
      required: true,
      errorMessage: 'Confirm your password.',
    },
    company: {
      required: true,
      regex:
        /^[^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+(?: [^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+)*$/,
      errorMessage: 'Enter a valid company name. No special characters allowed.',
    },
  };

  // ✅ Form Submission Handler
  const handleSignUp = () => {
    const isValid = validateFields(
      ['name', 'last-name', 'email', 'password1', 'password2', 'company'],
      validationRules,
      showAlert,
    );

    // ✅ Additional Validation for Password Match
    const password1 = document.getElementById('password1')?.value;
    const password2 = document.getElementById('password2')?.value;

    if (password1 !== password2) {
      document.getElementById('password1')?.classList.add('input-error');
      document.getElementById('password2')?.classList.add('input-error');
      showAlert('fail', 'Passwords do not match.');
      return;
    }

    if (isValid) {
      showAlert('success', 'Account created successfully!');
      console.log('Form Submitted Successfully');
    } else {
      showAlert('fail', 'Please ensure all fields are filled correctly.');
    }
  };

  return (
    <div className="container">
      <div className="row gap">
        <div className="d-flex radius-yes p-2 bg-yes col-12 flex-column gap">
          <div className="int-col-4 p-2">
            <h1 className="h1-title">Create user account</h1>
          </div>

          <div className="int-col-4 gap d-flex flex-row-to-col">
            <div className="int-col-2 p-2 border-yes radius-yes">
              <div className="int-col-4 p-2">
                <h3 className="h3-title">General data</h3>
              </div>
              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Name</div>
                  <div className="small-font">Enter superadmin name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="name"
                    label="Name"
                    placeholder="Enter name"
                    icon={<i className="fa-solid fa-user"></i>}
                    defaultValue=""
                    type="text"
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Last Name</div>
                  <div className="small-font">Enter superadmin last name </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="last-name"
                    label="Last name"
                    placeholder="Enter last name"
                    icon={<i className="fa-solid fa-user"></i>}
                    defaultValue=""
                    type="text"
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Email</div>
                  <div className="small-font">Enter superadmin email </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="email"
                    label="Email"
                    placeholder="Enter email"
                    icon={<i className="fa-solid fa-envelope"></i>}
                    defaultValue="my email"
                    type="text"
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Profile image</div>
                  <div className="small-font">Upload profile image</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <ConfirmationProvider>
                    <CustomFileUpload
                      multiple={false}
                      maxFileSize={1 * 1024 * 1024} // 2 MB
                      allowedFormats={['image/png', 'image/jpeg']}
                      showUploadedFiles={true}
                      allowFileDeletion={true}
                    />
                  </ConfirmationProvider>
                </div>
              </div>
            </div>

            <div className="int-col-2 p-2 border-yes radius-yes">
              <div className="int-col-4 p-2">
                <h2 className="h3-title">Company information</h2>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Company name</div>
                  <div className="small-font">Enter company name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="company"
                    label="Company"
                    placeholder="Enter company name"
                    icon={<i className="fa-solid fa-building"></i>}
                    defaultValue=""
                    type="text"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SECOND ROW */}

          <div className="int-col-4 gap d-flex flex-row-to-col">
            <div className="int-col-2 p-2 border-yes radius-yes">
              <div className="int-col-4 p-2">
                <h2 className="h3-title">Password</h2>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Enter password</div>
                  <div className="small-font">
                    Password shold have:
                    <ul>
                      <li>min 10 characters</li>
                      <li>min 2 symbols</li>
                      <li>min 2 numbers</li>
                    </ul>
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="password1"
                    label="Password"
                    placeholder="Enter Password"
                    defaultValue="SparkleDocs"
                    toggleVisibility={true}
                    type="password"
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Confirm password</div>
                  <div className="small-font">Enter the same password as above</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="password2"
                    label="Confirm Password"
                    placeholder="Confirm password"
                    defaultValue=""
                    toggleVisibility={true}
                    type="password"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="buttons-block w-100 justify-center d-flex flex-row flex-row-to-col">
            <Button variant="primary" size="large" onClick={handleSignUp}>
              Create account
            </Button>
            <Button background="color1" size="large" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrgSettings = ({ canEdit }) => (
  <ConfirmationProvider>
    <OrgSettingsPageContent canEdit={canEdit} />
  </ConfirmationProvider>
);

export default OrgSettings;
